import React, { useContext } from "react";

import { useNavigationScrollReady } from '../../navigation';

export interface ReceptionLayoutScrollOwner {
  key: string;
  ready: boolean;
  viewKey: string;
}

export interface ReceptionLayoutContextType {
  setTitle: (title: string) => void;
  setNoPadding: (noPadding: boolean) => void;
  registerScrollOwner: (
    owner: symbol,
    scrollOwner: ReceptionLayoutScrollOwner,
  ) => () => void;
  initialized: boolean;
}

export const ReceptionLayoutContext =
  React.createContext<ReceptionLayoutContextType>({
    setTitle: () => {},
    setNoPadding: () => {},
    registerScrollOwner: () => () => {},
    initialized: false,
  });

export const useReceptionLayout = () => useContext(ReceptionLayoutContext);

/**
 * Connect a reception page's terminal data readiness to both possible real
 * scroll owners: the reception shell's overflow main and the manager window.
 */
export function useReceptionLayoutScrollOwner(scrollOwner: ReceptionLayoutScrollOwner): void {
  const layout = useReceptionLayout();
  const ownerRef = React.useRef(Symbol('reception-layout-scroll-owner'));
  const stableScrollOwner = React.useMemo<ReceptionLayoutScrollOwner>(() => ({
    key: scrollOwner.key,
    ready: scrollOwner.ready,
    viewKey: scrollOwner.viewKey,
  }), [scrollOwner.key, scrollOwner.ready, scrollOwner.viewKey]);
  useNavigationScrollReady({ ready: scrollOwner.ready, viewKey: scrollOwner.viewKey });
  React.useLayoutEffect(
    () => layout.registerScrollOwner(ownerRef.current, stableScrollOwner),
    [layout, stableScrollOwner],
  );
}
